import React, { useRef, useState } from 'react';
import colors from '@packages/core/styles/colors';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Icon, Typography } from '@packages/ui/shared';
import { Form } from 'react-bootstrap';
import { TagInput } from '@web/components/tag-input';
import { PublicationForm } from '@web/vin-sessions/types';
import { UsePublicationFormReturnType } from './hooks/use-publication-form';
import { DtccTypeAhead } from './dtcc-typeahead';

const useStyles = createUseStyles({
    formLabel: {
        fontFamily: 'Roboto',
        fontWeight: 600,
        fontSize: 15,
        lineHeight: '24px',
        letterSpacing: '0%',
    },
});

interface Props {
    publicationInfo: PublicationForm;
    actions?: UsePublicationFormReturnType['actions'];
    publicationFormId?: string;
}

export const DocumentSearchMetaData = ({ publicationInfo, actions, publicationFormId }: Props) => {
    const { t } = useTranslation();
    const classes = useStyles();
    function handleAddKeyword(keyword: string) {
        if (keyword.trim() && !publicationInfo.keywords.includes(keyword.trim())) {
            const newKeywords = [...publicationInfo.keywords, keyword.trim()];
            actions?.handleAddKeywords(newKeywords);
            actions?.handleTagsChange(true);
        }
    }

    function handleRemoveKeyword(index: number) {
        const newKeywords = publicationInfo.keywords.filter((_, i) => i !== index);
        actions?.handleAddKeywords(newKeywords);
        actions?.handleTagsChange(true);
    }

    function handleAddToolNumber(toolNumber: string) {
        if (toolNumber.trim() && !publicationInfo.toolNumbers.includes(toolNumber.trim())) {
            const newToolNumbers = [...publicationInfo.toolNumbers, toolNumber.trim()];
            actions?.handleAddToolNumbers(newToolNumbers);
            actions?.handleTagsChange(true);
        }
    }

    function handleRemoveToolNumber(index: number) {
        const newToolNumbers = publicationInfo.toolNumbers.filter((_, i) => i !== index);
        actions?.handleAddToolNumbers(newToolNumbers);
        actions?.handleTagsChange(true);
    }

    return (
        <div style={{ padding: '20px 0px 20px 0px' }}>
            <Typography
                style={{
                    fontFamily: 'Roboto',
                    fontWeight: '600',
                    fontSize: 21,
                    lineHeight: 32,
                    letterSpacing: 0,
                }}
            >
                {t('stisPublications:documentInfo.headers.section4', 'Document Search Metadata')}
            </Typography>

            <Form>
                <Form.Group className="mb-3 mt-2">
                    <Form.Label className={classes.formLabel}>
                        {t('stisPublications:documentInfo.headers.keywords', 'Keywords')}
                    </Form.Label>
                    <TagInput
                        placeholder={t('stisPublications:documentInfo.placeholder.keywords', 'Add Keywords')}
                        tags={
                            Array.isArray(publicationInfo.keywords)
                                ? publicationInfo.keywords
                                : [publicationInfo.keywords]
                        }
                        styleProps={{ borderColor: colors.grayFive }}
                        handleAdd={handleAddKeyword}
                        handleRemove={handleRemoveKeyword}
                    />
                </Form.Group>

                <Form.Group className="mb-3 mt-2 d-flex justify-content-between">
                    <div style={{ width: '49%' }}>
                        <Form.Label className={classes.formLabel}>
                            {t('stisPublications:documentInfo.headers.dtcc', 'Add DTCs')}
                        </Form.Label>
                        <DtccTypeAhead publicationInfo={publicationInfo} actions={actions} />
                    </div>

                    <div style={{ width: '49%' }}>
                        <Form.Label className={classes.formLabel}>
                            {t('stisPublications:documentInfo.headers.toolNumbers', 'Tool Numbers')}
                        </Form.Label>
                        <TagInput
                            placeholder={t('stisPublications:documentInfo.placeholder.toolNumbers', 'Add tool numbers')}
                            tags={publicationInfo.toolNumbers}
                            styleProps={{ borderColor: colors.grayFive }}
                            handleAdd={handleAddToolNumber}
                            handleRemove={handleRemoveToolNumber}
                        />
                    </div>
                </Form.Group>
            </Form>
        </div>
    );
};
